import { DI, resolve } from 'aurelia';
import { onAuthStateChanged, signInWithCustomToken, signOut, type User } from 'firebase/auth';
import { IFirebase } from './firebase';
import { isKeychainAvailable, signBuffer, waitForKeychain } from './keychain';

export interface IAuth {
  user: User | null;
  username: string | null;
  readonly isSignedIn: boolean;
  ready: Promise<void>;
  keychainAvailable: boolean;
  busy: boolean;
  readonly lastUsername: string;
  detectKeychain(): Promise<boolean>;
  loginWithKeychain(username: string): Promise<void>;
  loginDev(username: string): Promise<void>;
  logout(): Promise<void>;
}

export const IAuth = DI.createInterface<IAuth>('IAuth', (x) => x.singleton(AuthService));

const LAST_USER_KEY = 'bingodash.username';

export class AuthService implements IAuth {
  user: User | null = null;
  username: string | null = null;
  keychainAvailable = false;
  busy = false;
  ready: Promise<void>;
  private fb = resolve(IFirebase);

  constructor() {
    this.ready = new Promise((res) => {
      let first = true;
      onAuthStateChanged(this.fb.auth, (u) => {
        this.user = u;
        this.username = u?.uid ?? null;
        if (first) { first = false; res(); }
      });
    });
  }

  get isSignedIn(): boolean {
    return this.user !== null;
  }

  get lastUsername(): string {
    try { return localStorage.getItem(LAST_USER_KEY) ?? ''; } catch { return ''; }
  }

  async detectKeychain(): Promise<boolean> {
    this.keychainAvailable = await waitForKeychain();
    return this.keychainAvailable;
  }

  async loginWithKeychain(usernameRaw: string): Promise<void> {
    const username = usernameRaw.trim().toLowerCase().replace(/^@/, '');
    if (!/^[a-z][a-z0-9\-.]{2,15}$/.test(username)) throw new Error('Enter a valid Hive username');
    if (!isKeychainAvailable()) throw new Error('Hive Keychain was not found. Install the browser extension or open this page in the Keychain mobile app.');
    this.busy = true;
    try {
      const ch = await this.fb.call<{ username: string }, { challengeId: string; message: string }>('loginChallenge', { username });
      const signed = await signBuffer(username, ch.message, 'Posting');
      if (!signed.success || typeof signed.result !== 'string') {
        throw new Error(signed.message ?? signed.error ?? 'Keychain declined the request');
      }
      const { token } = await this.fb.call<{ username: string; challengeId: string; signature: string }, { token: string }>('hiveLogin', {
        username, challengeId: ch.challengeId, signature: signed.result,
      });
      await signInWithCustomToken(this.fb.auth, token);
      try { localStorage.setItem(LAST_USER_KEY, username); } catch { /* ignore */ }
    } finally {
      this.busy = false;
    }
  }

  /** Emulator only: the server refuses this outside the Functions emulator. */
  async loginDev(usernameRaw: string): Promise<void> {
    const username = usernameRaw.trim().toLowerCase().replace(/^@/, '');
    this.busy = true;
    try {
      const { token } = await this.fb.call<{ username: string }, { token: string }>('devLogin', { username });
      await signInWithCustomToken(this.fb.auth, token);
      try { localStorage.setItem(LAST_USER_KEY, username); } catch { /* ignore */ }
    } finally {
      this.busy = false;
    }
  }

  async logout(): Promise<void> {
    await signOut(this.fb.auth);
  }
}
